import jQuery from "jquery";
import * as monaco from 'monaco-editor';
import * as PIXI from 'pixi.js';
import { AssemblyLanguageDebugger } from "../../compiler/assembly/debugger/AssemblyLanguageDebugger.js";
import { BreakpointManager } from "../../compiler/common/BreakpointManager.js";
import { Compiler } from "../../compiler/common/Compiler.js";
import { Debugger } from "../../compiler/common/debugger/Debugger.js";
import { Disassembler } from "../../compiler/common/disassembler/Disassembler.js";
import { Executable } from "../../compiler/common/Executable.js";
import { ActionManager } from "../../compiler/common/interpreter/ActionManager.js";
import { ExceptionMarker } from "../../compiler/common/interpreter/ExceptionMarker.js";
import { GraphicsManager } from "../../compiler/common/interpreter/GraphicsManager.js";
import { Interpreter } from "../../compiler/common/interpreter/Interpreter.js";
import { KeyboardManager } from "../../compiler/common/interpreter/KeyboardManager.js";
import { CompilerFile } from "../../compiler/common/module/CompilerFile.js";
import { CompilerWorkspace } from "../../compiler/common/module/CompilerWorkspace.js";
import { EditorOpenerProvider } from "../../compiler/common/monacoproviders/EditorOpenerProvider.js";
import { ErrorMarker } from "../../compiler/common/monacoproviders/ErrorMarker.js";
import { ProgramPointerManager } from "../../compiler/common/monacoproviders/ProgramPointerManager.js";
import { ProgrammingLanguage } from "../../compiler/common/programminglanguage/ProgrammingLanguage.js";
import { ProgrammingLanguageManager } from "../../compiler/common/programminglanguage/ProgrammingLanguageManager.js";
import { IPosition } from "../../compiler/common/range/Position.js";
import { IRange, Range } from "../../compiler/common/range/Range.js";
import { Repl } from "../../compiler/common/repl/Repl.js";
import { JavaDebugger } from "../../compiler/java/debugger/JavaDebugger.js";
import { base64ToBytes } from "../../tools/Base64.js";
import { Slider } from "../../tools/components/Slider.js";
import { downloadFile, findGetParameter, makeTabs, openContextMenu } from "../../tools/HtmlTools.js";
import { Tab } from "../../tools/TabManager.js";
import { BottomDiv } from "../main/gui/BottomDiv.js";
import { Editor } from "../main/gui/Editor.js";
import { FileManager } from "../main/gui/FileManager.js";
import { InputManager } from "../main/gui/InputManager.js";
import { PrintManager } from "../main/gui/PrintManager.js";
import { ProgramControlButtons } from "../main/gui/ProgramControlButtons.js";
import { RightDiv } from "../main/gui/RightDiv.js";
import { LoginMessages } from "../main/language/MainLanguage.js";
import { MainBase } from "../main/MainBase.js";
import { Settings } from "../settings/Settings.js";
import { SettingValues } from "../settings/SettingsMetadata.js";
import { SpritesheetData } from "../spritemanager/SpritesheetData.js";
import { GUIFile } from "../workspace/File.js";
import { Workspace } from "../workspace/Workspace.js";
import { ExportedWorkspace, WorkspaceExporter } from "../workspace/WorkspaceImporterExporter.js";
import { EmbeddedFileExplorer } from "./EmbeddedFileExplorer.js";
import { EmbeddedFullpageController } from "./EmbeddedFullpageController.js";
import { EmbeddedIndexedDB } from "./EmbeddedIndexedDB.js";
import { OnlineIDEAccessImpl, type OnRunExitListener } from "./EmbeddedInterface.js";
import { JOScript } from "./EmbeddedStarter.js";
import { SecureJSON } from "../../tools/SecureJSON.js";
import type { IThrowable } from "../../compiler/common/interpreter/ThrowableType.js";
import { JavaLanguage } from "../../compiler/java/JavaLanguage.js";

/**
 * Configuration options for the Java Online IDE in embedded mode.
 * https://learnj.de/doku.php?id=onlineide:integration:start#bedeutung_der_konfigurationsparameter
 */
type JavaOnlineConfig = {
    withFileList?: boolean,
    withPCode?: boolean,
    withConsole?: boolean,
    withErrorList?: boolean,
    withBottomPanel?: boolean,
    withClassDiagram?: boolean,
    speed?: number | "max",
    id?: string,
    hideStartPanel?: boolean,
    hideEditor?: boolean,
    libraries?: string[],
    jsonFilename?: string,
    spritesheetURL?: string,
    enableFileAccess?: boolean,
    enableRunExitStatusAccess?: boolean,
    settings?: SettingValues,
    workspaceURLParameterName?: string,
    cacheUserEdits?: boolean,

    programmingLanguage?: string,

}

export class MainEmbedded implements MainBase {

    config: JavaOnlineConfig;

    editor: Editor;

    currentWorkspace: Workspace;
    actionManager: ActionManager;

    language: ProgrammingLanguage;

    interpreter: Interpreter;
    $runDiv: JQuery<HTMLElement>;

    debugger: Debugger;
    $debuggerDiv: JQuery<HTMLElement>;
    $alternativeDebuggerDiv: JQuery<HTMLElement>;

    bottomDiv: BottomDiv;
    $filesDiv: JQuery<HTMLDivElement>
    ListDiv: JQuery<HTMLElement>;
    disassembler?: Disassembler;

    $hintDiv: JQuery<HTMLElement>;
    $monacoDiv: JQuery<HTMLElement>;
    $resetButton: JQuery<HTMLElement>;

    rightDiv: RightDiv;
    $rightDivInner: JQuery<HTMLElement>;

    fileExplorer: EmbeddedFileExplorer;

    debounceDiagramDrawing: any;

    indexedDB: EmbeddedIndexedDB;

    programControlButtons: ProgramControlButtons;

    breakpointManager: BreakpointManager;

    compileRunsAfterCodeReset: number = 0;

    settings: Settings;

    lastActiveFile?: GUIFile;

    horizontalSlider: Slider;
    verticalSlider: Slider;

    embeddedFullpageController: EmbeddedFullpageController;

    runExitListeners: OnRunExitListener[] = [];

    scriptListFromHtml: JOScript[] = [];


    constructor(private $outerDiv: JQuery<HTMLElement>) {

    }

    async init(scriptList: JOScript[]) {
        this.readConfig(this.$outerDiv);

        this.initGUI(this.$outerDiv);

        this.scriptListFromHtml = await this.tryLoadingWorkspaceFromURL() || scriptList;

        await this.processScriptsComingFromHtml();

        this.currentWorkspace.setLibraries(this.getCompiler());

        await this.loadUserSpritesheet();
        if (!this.config.hideStartPanel) {
            this.indexedDB = new EmbeddedIndexedDB();
            await this.indexedDB.open();

            if (this.config.id != null) {
                await this.readScriptsFromIndexedDB();
                if (this.fileExplorer) {
                    this.getCompiler().setFiles(this.fileExplorer.getFiles());
                    this.fileExplorer.selectFirstFileIfPresent();
                }
                if (this.fileExplorer == null) {
                    let files = this.currentWorkspace.getFiles();
                    this.getCompiler().setFiles(files);
                    if (files.length > 0) {
                        this.setFileActive(files[0]);
                    }
                }

                this.readClassDiagram();

                this.getCompiler().triggerCompile();

            }

            if (this.config.enableFileAccess || this.config.enableRunExitStatusAccess) {
                //@ts-ignore
                window.online_ide_access = new OnlineIDEAccessImpl();
                OnlineIDEAccessImpl.registerIDE(this);
            }
        }

    }

    isReadOnlyScript(identifier: string): boolean {
        return this.scriptListFromHtml.some(script => script.title === identifier && script.readOnly);
    }

    readClassDiagram() {
        if (!this.config.withClassDiagram) return;

        /*
         * In MainEmbedded.initGUI the classDiagram-Object is instantiated asynchronously to wait for
         * DOM-Construction first:
         * setTimeout(() => {
         *       this.classDiagram = new ClassDiagram(jQuery(this.classDiagramTab.bodyDiv), this.main);
         *   }, 100);
         * Therefore it can't be guaranteed that this object exists yet. The dirty solution is to wait for
         * it (see below).
         * 
         * TODO: Find a better way to create the classDiagram-object!
         */

        let f = () => {
            if (this.rightDiv?.classDiagram) {
                this.indexedDB.getScript(this.config.id + "-classDiagram", (serializedClassDiagram) => {
                    if (serializedClassDiagram != null) {
                        this.rightDiv.classDiagram.clear();
                        this.rightDiv.classDiagram.deserialize(JSON.parse(serializedClassDiagram));
                        this.drawClassDiagrams(false);
                    }
                })
            } else {
                setTimeout(() => {
                    f();
                }, 300);
            }
        }

        f();
    }

    async tryLoadingWorkspaceFromURL(): Promise<JOScript[] | undefined> {
        if (!this.config.workspaceURLParameterName) return;
        let url = findGetParameter(this.config.workspaceURLParameterName);
        if (!url) return;

        try {
            let response = await fetch(url);
            let workspaces = await response.json();
            if (!Array.isArray(workspaces)) workspaces = [workspaces];
            let exportedWorkspace: ExportedWorkspace = workspaces[0];
            let scriptList: JOScript[] = exportedWorkspace.modules.map(mo => ({
                title: mo.name,
                text: mo.text
            })
            );
            return scriptList;
        } catch (error) {
            console.log("Error retrieving or converting data from URL " + url + " to json.")
            console.error(error);
            return undefined;
        }
    }

    async processScriptsComingFromHtml() {

        this.fileExplorer?.removeAllFiles();

        this.initWorkspace(this.scriptListFromHtml);

        if (this.config.withFileList) {
            for (let file of this.currentWorkspace.getFiles()) {
                this.fileExplorer.addFile(file);
            }
            this.fileExplorer.selectFirstFileIfPresent();
        } else {
            this.setFileActive(this.currentWorkspace.getFirstFile());
        }

        this.getCompiler().triggerCompile();

    }


    readConfig($div: JQuery<HTMLElement>) {
        let configJson: string | object = $div.data("java-online");
        if (configJson != null && typeof configJson == "string") {
            this.config = JSON.parse(configJson.split("'").join('"'));
        } else {
            this.config = {}
        }

        if (typeof this.config.cacheUserEdits !== "boolean") {
            this.config.cacheUserEdits = true;
        }

        if (this.config.hideEditor == null) this.config.hideEditor = false;
        if (this.config.hideStartPanel == null) this.config.hideStartPanel = false;

        if (this.config.withBottomPanel == null) {
            this.config.withBottomPanel = this.config.withConsole || this.config.withPCode || this.config.withFileList || this.config.withErrorList;
        }

        if (this.config.hideEditor) {
            this.config.withBottomPanel = false;
            this.config.withClassDiagram = false;
            this.config.withFileList = false;
            this.config.withConsole = false;
            this.config.withPCode = false;
            this.config.withErrorList = false;
        }

        if (this.config.withBottomPanel) {
            if (this.config.withFileList == null) this.config.withFileList = true;
            if (this.config.withPCode == null) this.config.withPCode = true;
            if (this.config.withConsole == null) this.config.withConsole = true;
            if (this.config.withErrorList == null) this.config.withErrorList = true;
        }

        if (this.config.speed == null || (typeof this.config.speed === 'number' && this.config.speed < 0)) this.config.speed = "max";
        if (this.config.libraries == null) this.config.libraries = [];
        if (this.config.jsonFilename == null) this.config.jsonFilename = "workspace.json";

    }

    setFileActive(file: GUIFile) {

        if (!file) return;

        if (this.lastActiveFile) {
            this.lastActiveFile.saveViewState(this.getMainEditor());
        }

        if (this.config.withFileList) {
            this.fileExplorer.markAsSelectedButDontInvokeCallback(file);
        }

        this.lastActiveFile = file;

        /**
         * WICHTIG: Die Reihenfolge der beiden Operationen ist extrem wichtig.
         * Falls das Model im readonly-Zustand gesetzt wird, funktioniert <Strg + .>
         * nicht und die Lightbulbs werden nicht angezeigt, selbst dann, wenn
         * später readonly = false gesetzt wird.
         */
        this.getMainEditor().updateOptions({
            readOnly: file.readOnly || false,
            lineNumbersMinChars: 4
        });

        try {
            this.editor.editor.setModel(file.getMonacoModel());
        } catch (e) {
            console.log("Caught!");
        }

        file.restoreViewState(this.getMainEditor());

        this.disassembler?.disassemble();

    }

    eraseDokuwikiSearchMarkup(text: string): string {
        return text.replace(/<span class="search\whit">(.*?)<\/span>/g, "$1");
    }

    async readScriptsFromIndexedDB(): Promise<void> {

        return new Promise<void>((resolve, reject) => {
            let files = this.currentWorkspace.getFiles();
            files.forEach(f => {
                f.getMonacoModel();
                f.setSaved(true);
            })

            if (!this.config.cacheUserEdits) {
                resolve();
                return;
            }


            let that = this;

            this.indexedDB.getScript(this.config.id, (scriptListJSon) => {
                if (scriptListJSon == null) {
                    setTimeout(() => {
                        setInterval(() => {
                            that.saveScripts();
                        }, 1000);
                    }, 2000);
                    resolve();
                } else {

                    let scriptList: string[] = JSON.parse(scriptListJSon);
                    let countDown = scriptList.length;

                    for (let file of files.slice()) {
                        that.fileExplorer?.removeFile(file, false);  // calls MainEmbedded.removeFile subsequently
                    }
                    that.currentWorkspace.removeAllFiles();

                    for (let name of scriptList) {

                        let scriptId = this.config.id + name;
                        this.indexedDB.getScript(scriptId, (script) => {
                            if (script != null) {

                                script = this.eraseDokuwikiSearchMarkup(script);

                                let file = new GUIFile(this, name, script);
                                file.getMonacoModel();
                                file.setSaved(true);
                                file.readOnly = that.isReadOnlyScript(name);

                                that.fileExplorer?.addFile(file);
                                that.currentWorkspace.addFile(file);
                                that.showResetButton();

                                // console.log("Retrieving script " + scriptId);
                            }
                            countDown--;
                            if (countDown == 0) {
                                setInterval(() => {
                                    that.saveScripts();
                                }, 1000);
                                resolve();
                            }
                        })

                    }

                }


            });
        });

    }

    saveScripts() {

        let files = this.currentWorkspace.getFiles();

        let scriptList: string[] = [];
        let oneNotSaved: boolean = false;

        files.forEach(file => oneNotSaved = oneNotSaved || !file.isSaved());

        if (oneNotSaved) {

            for (let file of files) {
                scriptList.push(file.name);
                let scriptId = this.config.id + file.name;
                this.indexedDB.writeScript(scriptId, file.getText());
                file.setSaved(true);
                // console.log("Saving script " + scriptId);
            }

            this.indexedDB.writeScript(this.config.id, JSON.stringify(scriptList));

        }

        let classDiagram = this.rightDiv?.classDiagram;
        if (classDiagram && classDiagram.dirty) {
            this.indexedDB.writeScript(this.config.id + "-classDiagram", JSON.stringify(classDiagram.serialize()));
            classDiagram.dirty = false;
        }

    }

    deleteScriptsInDB() {
        this.indexedDB.getScript(this.config.id, (scriptListJSon) => {
            if (scriptListJSon == null) {
                return;
            } else {

                let scriptList: string[] = JSON.parse(scriptListJSon);

                for (let name of scriptList) {

                    let scriptId = this.config.id + name;
                    this.indexedDB.removeScript(scriptId);
                }

                this.indexedDB.removeScript(this.config.id);

            }


        });

        this.indexedDB.removeScript(this.config.id + "-classDiagram");

    }

    initWorkspace(scriptList: JOScript[]) {

        this.currentWorkspace = new Workspace("Embedded-Workspace", this, 0);
        this.currentWorkspace.settings.libraries = this.config.libraries;
        this.currentWorkspace.id = 0; // class diagram needs this
        this.currentWorkspace.settings.language = this.getCurrentProgrammingLanguage().name;

        let i = 0;
        for (let script of scriptList) {
            this.addFile(script);
        }

        this.switchProgrammingLanguage(this.currentWorkspace.settings.language);

    }

    addFile(script: JOScript): GUIFile {

        let file = new GUIFile(this, script.title, script.text);
        file.readOnly = script.readOnly || false;
        file.id = this.currentWorkspace.getFiles().length;

        this.currentWorkspace.addFile(file);

        let that = this;

        file.getMonacoModel().onDidChangeContent(() => {
            that.considerShowingCodeResetButton();
        });

        return file;
    }

    removeFile(file: GUIFile) {
        this.currentWorkspace.removeFile(file);
        this.getCompiler()?.triggerCompile();
    }


    initGUI($div: JQuery<HTMLElement>) {

        // let $leftDiv = jQuery('<div class="joe_leftDiv"></div>');

        $div.css({
            "background-image": "none",
            "background-size": "100%"
        })

        let $centerDiv = jQuery('<div class="joe_centerDiv"></div>');
        let $resetModalWindow = this.makeCodeResetModalWindow($div);

        let $rightDiv = this.makeRightDiv();

        let $editorDiv = jQuery('<div class="joe_editorDiv"></div>');
        this.$monacoDiv = jQuery('<div class="joe_monacoDiv"></div>');
        this.$hintDiv = jQuery('<div class="joe_hintDiv jo_scrollable"></div>');
        this.$resetButton = jQuery('<div class="joe_resetButton jo_button jo_active" title="Code auf Ausgangszustand zurücksetzen">Code Reset</div>');

        $editorDiv.append(this.$monacoDiv, this.$hintDiv, this.$resetButton);

        // let $bracketErrorDiv = this.makeBracketErrorDiv();
        // $editorDiv.append($bracketErrorDiv);

        this.$resetButton.hide();

        this.$resetButton.on("click", () => { $resetModalWindow.show(); })

        this.$hintDiv.hide();

        let $controlsDiv = jQuery('<div class="joe_controlsDiv"></div>');
        let $bottomDivInner = jQuery('<div class="joe_bottomDivInner"></div>');

        let $buttonOpen = jQuery('<label type="file" class="img_open-file jo_button jo_active"' +
            'style="margin-right: 8px;" title="Workspace aus Datei laden"><input type="file" style="display:none"></label>');

        let that = this;

        $buttonOpen.find('input').on('change', (event) => {
            //@ts-ignore
            var files: FileList = event.originalEvent.target.files;
            that.loadWorkspaceFromFile(files[0]);
        })

        let $buttonSave = jQuery('<div class="img_save-dark jo_button jo_active"' +
            'style="margin-right: 8px;" title="Workspace in Datei speichern"></div>');


        $buttonSave.on('click', () => { that.saveWorkspaceToFile() });

        $controlsDiv.append($buttonOpen, $buttonSave);



        if (this.config.withBottomPanel) {
            let $bottomDiv = jQuery('<div class="joe_bottomDiv"></div>');
            $bottomDivInner.append($controlsDiv);

            $bottomDiv.append($bottomDivInner);
            if (this.config.withFileList) {
                this.$filesDiv = this.makeFilesDiv();
                $bottomDiv.prepend(this.$filesDiv);
                new Slider(this.$filesDiv[0], false, false, () => { });
                this.fileExplorer = new EmbeddedFileExplorer(this.$filesDiv, this);
            }
            makeTabs($bottomDivInner);


            $centerDiv.append($editorDiv, $bottomDiv);
            this.verticalSlider = new Slider($bottomDiv[0], true, true, () => { this.editor.editor.layout(); });
        } else {
            $centerDiv.prepend($editorDiv);
        }

        if (!this.config.withBottomPanel) {
            if (this.config.hideEditor) {
                $rightDiv.prepend($controlsDiv);
            } else {
                $centerDiv.prepend($controlsDiv);
                $controlsDiv.addClass('joe_controlPanel_top');
                $editorDiv.css({
                    'position': 'relative',
                    'height': '1px'
                });
            }
        }

        $div.addClass('joe_javaOnlineDiv');
        $div.append($centerDiv, $rightDiv);

        if (!this.config.hideEditor) {
            this.horizontalSlider = new Slider($rightDiv[0], true, false, () => {
                this.editor.editor.layout();
            });
        }

        this.actionManager = new ActionManager($div);
        this.actionManager.init();

        this.editor = new Editor(this, false, true);
        this.editor.initGUI(this.$monacoDiv);
        this.$monacoDiv.find('.monaco-editor').css('z-index', '10');

        if ($div.attr('tabindex') == null) $div.attr('tabindex', "0");

        if (this.config.withBottomPanel) {
            this.bottomDiv = new BottomDiv(this, $bottomDivInner, this.config.withConsole, this.config.withPCode, this.config.withErrorList, true);
            this.bottomDiv.initGUI();
        }

        let graphicsDiv = this.$rightDivInner.find('.jo_graphics')[0];
        let coordinatesDiv = <HTMLDivElement>this.$rightDivInner.find('.jo_coordinates')[0];

        let breakpointManager = new BreakpointManager(this);
        let inputManager = new InputManager(this.$runDiv, this);
        let printManager = new PrintManager(this.$runDiv, this);
        let fileManager = new FileManager(this);

        let keyboardManager = new KeyboardManager(jQuery('html'), this);
        let programPointerManager = new ProgramPointerManager(this);

        this.interpreter = new Interpreter(
            printManager, this.actionManager,
            new GraphicsManager(graphicsDiv, coordinatesDiv), keyboardManager,
            breakpointManager,
            programPointerManager, inputManager,
            fileManager, new ExceptionMarker(this), this);

        this.interpreter.eventManager.on("done", () => {
            this.runExitListeners.forEach((listener) => {
                listener({
                    exitCode: this.interpreter.exitStatus,
                    output: this.interpreter.printManager?.getLog() || "",
                    exception: this.interpreter.exception,
                    testProgress: this.language instanceof JavaLanguage ? this.language.getTestRunner().getProgress() : undefined
                });
            });
        })

        this.interpreter.eventManager.on("start", () => {
            if (this.runExitListeners.length > 0) {
                this.interpreter.printManager?.startLogging();
            } else {
                this.interpreter.printManager?.stopLogging();
            }
        });

        /**
         * Compiler and Repl are fields of language!
        */
        let errorMarker = new ErrorMarker();
        ProgrammingLanguageManager.getInstance().registerMain(this, errorMarker);
        this.switchProgrammingLanguage(this.config.programmingLanguage || "Java");

        // if (this.config.withBottomPanel) {
        //     new JUnitTestrunner(this, this.bottomDiv.jUnitTab.bodyDiv);
        // }

        // this.getCompiler().triggerCompile();

        if (this.config.withBottomPanel && this.config.withPCode) {
            this.disassembler = new Disassembler(this.bottomDiv.disassemblerTab.bodyDiv, this);
        }

        this.programControlButtons = new ProgramControlButtons($controlsDiv, this.interpreter, this.actionManager);

        new EditorOpenerProvider(this);

        let $infoButton = jQuery('<div class="jo_button jo_active img_ellipsis-dark" style="margin-left: 8px"></div>');
        $infoButton[0].title = 'Über die Online-IDE...';
        $controlsDiv.append($infoButton);

        $infoButton.on('mousedown', (ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            openContextMenu([{
                caption: "Über die Online-IDE ...",
                link: "https://www.online-ide.de",
                callback: () => {
                    // nothing to do.
                }
            }], ev.pageX + 2, ev.pageY + 2);
        });

        this.embeddedFullpageController = new EmbeddedFullpageController(this, this.$outerDiv[0], $controlsDiv[0]);

        setTimeout(() => {
            this.editor.editor.layout();
            this.programControlButtons.speedControl.setSpeedInStepsPerSecond(this.config.speed);
        }, 200);

        if (this.config.hideEditor) {
            $centerDiv.hide();
            $rightDiv.css("flex", "1");
            if (!this.config.hideStartPanel) {
                $div.find(".joe_rightDivInner").css('height', 'calc(100% - 24px)');
                $div.find(".joe_controlsDiv").css('padding', '2px');
                $div.find(".jo_speedcontrol-outer").css('z-index', '10');
            } else {
                $div.find(".joe_controlsDiv").hide();
            }
        }

        if (!PIXI.isWebGLSupported()) {
            printManager?.print(LoginMessages.webGLNotSupported(), true, 0xff2020);
        }


        // this.interpreter.eventManager.on("stateChanged", (oldState: SchedulerState, newState: SchedulerState) => {
        //     if (newState == SchedulerState.paused) {
        //         this.$debuggerDiv.show();
        //         this.$alternativeDebuggerDiv.hide();
        //         return;
        //     } else if (!(oldState == SchedulerState.paused && newState == SchedulerState.running)) {
        //         this.$debuggerDiv.hide();
        //         this.$alternativeDebuggerDiv.show();
        //     }
        // })

    }

    hideDebugger() {
        this.$debuggerDiv.hide();
        this.$alternativeDebuggerDiv.show();
    }

    showDebugger() {
        this.$debuggerDiv.show();
        this.$alternativeDebuggerDiv.hide();
        this.debugger?.show();
    }

    // makeBracketErrorDiv(): JQuery<HTMLElement> {
    //     return jQuery(`
    //     <div class="jo_parenthesis_warning" title="Klammerwarnung!" style="bottom: 55px">
    //     <div class="jo_warning_light"></div>
    //     <div class="jo_pw_heading">{ }</div>
    //     <div title="Letzten Schritt rückgängig"
    //         class="jo_pw_undo img_undo jo_button jo_active"></div>
    //     </div>
    //     `);
    // }

    makeCodeResetModalWindow($parent: JQuery<HTMLElement>): JQuery<HTMLElement> {
        let $window = jQuery(
            `
            <div class="joe_codeResetModal">
            <div style="flex: 1"></div>
            <div style="display: flex">
                <div style="flex: 1"></div>
                <div style="padding-left: 30px;">
                <div style="color: red; margin-bottom: 10px; font-weight: bold">Warnung:</div>
                <div>Soll der Code wirklich auf den Ausgangszustand zurückgesetzt werden?</div>
                <div>Alle von Dir gemachten Änderungen werden damit verworfen.</div>
                </div>
                <div style="flex: 1"></div>
            </div>
            <div class="joe_codeResetModalButtons">
            <div class="joe_codeResetModalCancel jo_button jo_active">Abbrechen</div>
            <div class="joe_codeResetModalOK jo_button jo_active">OK</div>
            </div>
            <div style="flex: 2"></div>
            </div>
        `
        );

        $window.hide();

        $parent.append($window);

        this.$outerDiv.find(".joe_codeResetModalCancel").on("click", () => {
            $window.hide();
        });

        this.$outerDiv.find(".joe_codeResetModalOK").on("click", async () => {

            await this.processScriptsComingFromHtml();
            this.currentWorkspace.getFiles().forEach(f => f.setSaved(true));
            this.deleteScriptsInDB();

            $window.hide();
            this.$resetButton.hide();
            this.compileRunsAfterCodeReset = 1;

        });

        return $window;
    }

    makeFilesDiv(): JQuery<HTMLDivElement> {


        let $filesDiv: JQuery<HTMLDivElement> = jQuery('<div class="joe_bottomDivFiles"></div>');

        return $filesDiv;
    }

    considerShowingCodeResetButton() {
        this.compileRunsAfterCodeReset++;
        if (this.compileRunsAfterCodeReset == 3) {
            this.$resetButton.fadeIn(1000);
        }
    }

    drawClassDiagrams(onlyUpdateIdentifiers: boolean) {
        clearTimeout(this.debounceDiagramDrawing);
        this.debounceDiagramDrawing = setTimeout(() => {
            this.rightDiv?.classDiagram?.drawDiagram(this.currentWorkspace, onlyUpdateIdentifiers);
        }, 500);
    }

    async saveWorkspaceToFile() {
        let filename: string = prompt("Bitte geben Sie den Dateinamen ein", this.config.jsonFilename);
        if (filename == null) {
            alert("Der Dateiname ist leer, daher wird nichts gespeichert.");
            return;
        }
        if (!filename.endsWith(".json")) filename = filename + ".json";
        let ws = this.currentWorkspace;
        let exportedWorkspace = await WorkspaceExporter.exportWorkspace(ws);
        downloadFile(exportedWorkspace, filename)
    }

    loadWorkspaceFromFile(file: globalThis.File) {
        let that = this;
        if (file == null) return;
        var reader = new FileReader();
        reader.onload = async (event) => {
            let text: string = <string>event.target.result;
            // if (!text.startsWith("{")) {
            //     alert(`<div>Das Format der Datei ${file.name} passt nicht.</div>`);
            //     return;
            // }

            let ew: ExportedWorkspace = JSON.parse(text);

            if (Array.isArray(ew)) {
                if (ew.length == 0) {
                    alert(`<div>Das Format der Datei ${file.name} passt nicht.</div>`);
                    return;
                }
                ew = ew[0];
            }

            if (ew.modules == null || ew.name == null || ew.settings == null) {
                alert(`<div>Das Format der Datei ${file.name} passt nicht.</div>`);
                return;
            }

            let ws: Workspace = new Workspace(ew.name, this, 0);
            ws.settings = ew.settings;
            ws.id = 0; // class diagram needs this

            for (let mo of ew.modules) {
                let f = new GUIFile(this, mo.name, mo.text);
                ws.addFile(f);
            }

            that.currentWorkspace = ws;

            if (ew.spritesheetBase64) {
                let zipFile = base64ToBytes(ew.spritesheetBase64);
                await new SpritesheetData().initializeSpritesheetForWorkspace(ws, this, zipFile);
            }

            if (that.fileExplorer != null) {
                that.fileExplorer.removeAllFiles();
                ws.getFiles().forEach(file => that.fileExplorer.addFile(file));
                that.fileExplorer.selectFirstFileIfPresent();
            } else {
                this.setFileActive(this.currentWorkspace.getFirstFile());
            }

            this.getCompiler().triggerCompile();

            that.saveScripts();

            this.showResetButton();

        };
        reader.readAsText(file);

    }

    showResetButton() {
        this.$resetButton.fadeIn(1000);
    }

    makeRightDiv(): JQuery<HTMLElement> {

        let $rightDiv = jQuery('<div class="joe_rightDiv"></div>');
        this.$rightDivInner = jQuery('<div class="joe_rightDivInner"></div>');
        $rightDiv.append(this.$rightDivInner);
        this.$outerDiv.append($rightDiv);

        this.rightDiv = new RightDiv(this, this.$outerDiv[0], this.config.withClassDiagram);
        this.rightDiv.initGUI();

        this.$debuggerDiv = jQuery(`<div class="joe_debuggerDiv"></div>`);
        this.$alternativeDebuggerDiv = jQuery(`
        <div class="jo_alternativeText jo_scrollable">
        <div style="font-weight: bold">Tipp:</div>
        Die Variablen sind nur dann sichtbar, wenn das Programm
        <ul>
        <li>im Einzelschrittmodus ausgeführt wird(Klick auf <span class="img_step-over-dark jo_inline-image"></span>),</li>
        <li>an einem Breakpoint hält (Setzen eines Breakpoints mit Mausklick links neben den Zeilennummern und anschließendes Starten des Programms mit
            <span class="img_start-dark jo_inline-image"></span>) oder </li>
            <li>in sehr niedriger Geschwindigkeit ausgeführt wird (weniger als 10 Schritte/s).
            </ul>
            </div>
            `);

        if (!this.config.hideEditor) {
            let debuggerTab = new Tab('Debugger', 'Debugger', ['jo_scrollable', 'jo_editorFontSize', 'jo_variablesTab']);
            this.rightDiv.tabManager.addTab(debuggerTab);

            let $vd = jQuery(debuggerTab.bodyDiv);


            this.$debuggerDiv.hide();
            $vd.append(this.$debuggerDiv, this.$alternativeDebuggerDiv);
        }

        this.$runDiv = jQuery(this.rightDiv.outputTab.bodyDiv);

        return $rightDiv;
    }

    async loadUserSpritesheet() {
        if (this.config.spritesheetURL != null) {

            let spritesheet = new SpritesheetData();

            await spritesheet.initializeSpritesheetForWorkspace(this.currentWorkspace, this, this.config.spritesheetURL);

        }
    }

    showFile(file?: CompilerFile): void {
        if (!file) return;
        this.fileExplorer?.selectFile(<GUIFile>file, false);
    }

    getDisassembler(): Disassembler | undefined {
        return this.disassembler;
    }

    showJUnitDiv(): void {
        this.bottomDiv?.showJunitTab();
    }

    showProgramPosition(file?: CompilerFile, positionOrRange?: IPosition | IRange, setCursor: boolean = true) {
        this.showFile(file);
        if (!positionOrRange) return;
        if (positionOrRange["startLineNumber"]) positionOrRange = Range.getStartPosition(<IRange>positionOrRange);
        if (setCursor) this.getMainEditor().setPosition(<IPosition>positionOrRange);
        this.getMainEditor().revealPositionInCenterIfOutsideViewport(<IPosition>positionOrRange);
        this.getMainEditor().focus();
    }


    markFilesAsStartable(files: GUIFile[], active: boolean) {
        this.fileExplorer?.markFilesAsStartable(files, active);
    }

    onStartFileClicked(file: GUIFile) {
        this.interpreter.start(file);
    }

    switchProgrammingLanguage(languageName: string) {
        let language = ProgrammingLanguageManager.getInstance().getLanguageByName(languageName);
        if (language == this.language) return;
        this.language?.disable(this);
        this.language = language;
        this.language.enable(this);

        this.getCompiler().eventManager.on("compilationFinishedWithNewExecutable", this.onCompilationFinished, this);

        if (this.$debuggerDiv) {
            let dd = <HTMLDivElement>this.$debuggerDiv[0];

            this.debugger?.remove();
            dd.innerHTML = "";
            switch (this.language.getDebuggerType()) {
                case "java":
                    this.debugger = new JavaDebugger(dd, !this.isEmbedded(), this);
                    break;
                case "assembly":
                    this.debugger = new AssemblyLanguageDebugger(dd, !this.isEmbedded(), this);
                    break;
            }

            this.debugger.hide();
        }

    }

    setHorizontalSliderPosition(fraction: number): void {
        this.horizontalSlider?.setPosition(fraction);
    }

    registerOnRunExitListener(listener: OnRunExitListener) {
        this.runExitListeners.push(listener);
    }

    isEmbedded(): boolean { return true; }

    getCompiler(): Compiler {
        return this.language.getCompiler(this);
    }
    getInterpreter(): Interpreter {
        return this.interpreter;
    }
    getCurrentWorkspace(): Workspace {
        return this.currentWorkspace;
    }
    getDebugger(): Debugger {
        return this.debugger;
    }
    getMonacoEditor(): monaco.editor.IStandaloneCodeEditor {
        return this.editor.editor;
    }

    getRightDiv(): RightDiv {
        return this.rightDiv;
    }

    getBottomDiv(): BottomDiv {
        return this.bottomDiv;
    }

    getActionManager(): ActionManager {
        return this.actionManager;
    }

    addWorkspace(ws: CompilerWorkspace): void {
        // not used
    }

    getCurrentProgrammingLanguage(): ProgrammingLanguage {
        return this.language;
    }

    getRepl(): Repl {
        return this.language?.getRepl(this);
    }

    getMainEditor(): monaco.editor.IStandaloneCodeEditor {
        return this.editor.editor;
    }

    getReplEditor(): monaco.editor.IStandaloneCodeEditor {
        return this.bottomDiv?.console.editor;
    }

    getSettings(): Settings {
        let userSettings = this.config.settings || {};

        if (!this.settings) {
            this.settings = new Settings(undefined, SecureJSON.stringify(userSettings), undefined, undefined);
        }
        return this.settings;
    }

    onCompilationFinished(executable: Executable | undefined): void {
        this.interpreter.setExecutable(executable);

        if (this.bottomDiv && this.fileExplorer) {
            let errors = this.bottomDiv?.errorManager?.showErrors(this.currentWorkspace);
            this.fileExplorer.renderErrorCount(this.currentWorkspace, errors);
        }

        this.drawClassDiagrams(!this.rightDiv.isClassDiagramActive());

    }

    adjustWidthToWorld(): void {
        this.rightDiv.adjustWidthToWorld();
    }

}


